import {
  ICallRecordingData,
  IClientPhoneApiCall,
  IClientPhoneCall,
  IPhoneCallsPage,
  IPhoneCallsFilters,
  IMakeExternalCallResponse,
  IRestApiService,
} from '../types';
import { PhoneTracker } from '../services/analytics/apiTrackers';

class ClientPhoneApiCall implements IClientPhoneApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getClientPhoneCalls(ownerId: string | number, studioClientId: string | number): Promise<IClientPhoneCall[]> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/client/${studioClientId}`;

    return this.restApiService
      .get<IClientPhoneCall[]>(url)
      .then((result) => {
        PhoneTracker.trackGetClientPhoneCalls({
          ownerId,
          studioClientId,
          success: true,
          duration: Date.now() - startTime,
          callsCount: result?.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetClientPhoneCalls({
          ownerId,
          studioClientId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getPendingClientPhoneCalls(ownerId: string | number, pendingClientId: string | number): Promise<IClientPhoneCall[]> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/pending-client/${pendingClientId}`;

    return this.restApiService
      .get<IClientPhoneCall[]>(url)
      .then((result) => {
        PhoneTracker.trackGetPendingClientPhoneCalls({
          ownerId,
          pendingClientId,
          success: true,
          duration: Date.now() - startTime,
          callsCount: result?.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetPendingClientPhoneCalls({
          ownerId,
          pendingClientId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getPhoneCalls(ownerId: string | number, from: any, to: any, callType: string): Promise<IClientPhoneCall[]>;
  getPhoneCalls(
    ownerId: string | number,
    from: any,
    to: any,
    callType: string,
    page: number,
    pageSize: number,
    filters?: IPhoneCallsFilters
  ): Promise<IPhoneCallsPage>;
  getPhoneCalls(
    ownerId: string | number,
    from: any,
    to: any,
    callType: string,
    page?: number,
    pageSize?: number,
    filters?: IPhoneCallsFilters
  ): Promise<IClientPhoneCall[] | IPhoneCallsPage> {
    const startTime = Date.now();
    const query = new URLSearchParams({
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY'),
      callType,
    });
    if (page && pageSize) {
      query.set('page', String(page));
      query.set('pageSize', String(pageSize));
    }
    if (filters?.internalNumbers?.length) query.set('internalNumbers', filters.internalNumbers.join(','));
    if (filters?.search) query.set('search', filters.search);
    const url = `client-phone-call/owner/${ownerId}/filtered?${query.toString()}`;

    return this.restApiService
      .get<IClientPhoneCall[] | IPhoneCallsPage>(url)
      .then((result) => {
        PhoneTracker.trackGetPhoneCalls({
          ownerId,
          from,
          to,
          callType,
          success: true,
          duration: Date.now() - startTime,
          callsCount: Array.isArray(result) ? result.length : result?.items.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetPhoneCalls({
          ownerId,
          from,
          to,
          callType,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  // Background version of getPhoneCalls above - same from/to/callType query shape, fixes the
  // "[object Object]" bug the built-in mui-datatables CSV had for the client column
  // (see PhoneCallsTable.tsx's options.download comment).
  startPhoneCallsExport(
    ownerId: string | number,
    from: any,
    to: any,
    callType: string
  ): Promise<{ jobId: string; downloadUrl: string }> {
    const url = `client-phone-call/owner/${ownerId}/filtered/export-async?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}&callType=${callType}`;

    return this.restApiService.post(url);
  }

  getPhoneCallRecord(ownerId: string | number, callId: string | number): Promise<ICallRecordingData> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/call-record/${callId}`;

    return this.restApiService
      .get<ICallRecordingData>(url)
      .then((result) => {
        PhoneTracker.trackGetPhoneCallRecord({
          ownerId,
          callId,
          success: true,
          duration: Date.now() - startTime,
          hasRecording: result?.recordingUrl ? true : false,
          recordingDuration: result?.duration || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetPhoneCallRecord({
          ownerId,
          callId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  makeExternalCall(
    ownerId: string | number,
    internalNumber: string,
    externalNumber: string
  ): Promise<IMakeExternalCallResponse> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/make-external-call`;

    return this.restApiService
      .post<IMakeExternalCallResponse>(url, { internalNumber, externalNumber })
      .then((result) => {
        PhoneTracker.trackMakeExternalCall({
          ownerId,
          internalNumber,
          externalNumber,
          success: true,
          duration: Date.now() - startTime,
          callId: result?.callId,
          callStatus: result?.status,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackMakeExternalCall({
          ownerId,
          internalNumber,
          externalNumber,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }
}

export default ClientPhoneApiCall;
